import { useState, useEffect, useRef, useMemo } from "react";
import RestaurantList from "../components/common/restaurant-list";
import ViewEmployeesPanel from "../components/common/view-employees-panel";
import LessonProgressPanel from "../components/common/lesson-progress-panel";
import ManageRestaurantPanel from "../components/common/manage-restaurant-panel";
import AddRestaurantPanel from "../components/common/add-restaurant-modal";
import ManageMenuPanel from "../components/common/manage-menu-panel";
import { RestaurantsService } from "../services/Restaurants";
import { DashboardService } from "../services/dashboard";
import { bulkUploadLessons } from "../services/lessonProgress";
import authService from "../services/authService";
import { MenuService } from "../services/MenuService";
import { Search, ChevronDown, Upload, Users, Check, RotateCcw, ChevronRight, ChevronLeft } from "lucide-react";
import { PiForkKnifeFill } from "react-icons/pi";
import usaStates from "../usaStates";
import { Snackbar, Alert } from "@mui/material";

export default function RestaurantManagement() {
  const userRole = localStorage.getItem("userRole");
  const user = authService.getCurrentUser();

  const [activeTab, setActiveTab] = useState("all");
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [jsonPreview, setJsonPreview] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [dishAnalyticsData, setDishAnalyticsData] = useState([]);
  const [wineAnalyticsData, setWineAnalyticsData] = useState([]);
  const [beerAnalyticsData, setBeerAnalyticsData] = useState(null);
  const [userProfile, setUserProfile] = useState();

  // Dashboard aggregated stats from API
  const [dashboardStats, setDashboardStats] = useState({
    trainingRate: 0,
    engagementRate: 0,
    missedUsersCount: 0,
    lastMenuUpdateInDays: 0,
    averageChapterScore: 0,
    totalRestaurants: 0,
    totalEmployees: 0,
    activeRestaurantsCount: 0,
  });

  // Local search term for client-side filtering
  const [searchTerm, setSearchTerm] = useState("");

  // Filter States
  const [selectedCity, setSelectedCity] = useState("State");
  const [selectedCuisine, setSelectedCuisine] = useState("Cuisine");
  const [selectedRisk, setSelectedRisk] = useState("Training Risk");
  const [selectedSort, setSelectedSort] = useState("Alphabetical");
  const [selectedPeriod, setSelectedPeriod] = useState("Any");
  const [selectedRegion, setSelectedRegion] = useState("Region");
  const [selectedDirector, setSelectedDirector] = useState("Director");
  const [selectedSubscriptionType, setSelectedSubscriptionType] = useState("Subscription Type");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Custom Dropdown Component for Premium Options Styling
  const CustomDropdown = ({ label, options, value, onChange, className, searchable = false, maxHeight = "max-h-[160px]" }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const dropdownRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
          setSearchTerm("");
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredOptions = searchable && searchTerm
      ? options.filter(option => option.toLowerCase().includes(searchTerm.toLowerCase()))
      : options;

    return (
      <div className={`relative ${className}`} ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`w-full bg-white border rounded-xl px-4 py-2.5 text-[13px] font-medium flex items-center justify-between transition-all duration-200 ${value !== label && !value.includes(label) ? "border-[#881337] text-[#881337]" : "border-gray-200 text-gray-400"
            } hover:border-gray-300 hover:bg-gray-50/50`}
        >
          <span className="truncate pr-2">{value}</span>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-300 flex-shrink-0 ${isOpen ? "rotate-180" : ""}`} />
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 mt-2 bg-white border border-orange-50/50 rounded-xl shadow-xl z-50 w-[280px] sm:w-full">
            {searchable && (
              <div className="p-3 border-b border-gray-100">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search..."
                    className="w-full bg-gray-50 border border-gray-200 rounded-lg py-2 pl-9 pr-3 text-xs focus:outline-none focus:ring-2 focus:ring-[#881337]/5 focus:border-[#881337]"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              </div>
            )}
            <div className={`${maxHeight} overflow-y-auto py-2 scrollbar-thin scrollbar-thumb-gray-200 ${searchable ? '' : ''}`}>
              {filteredOptions.length > 0 ? (
                filteredOptions.map((option, idx) => (
                  <button
                    key={`${option}-${idx}`}
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                      setSearchTerm("");
                    }}
                    className={`w-full text-left px-5 py-2.5 text-[13px] font-bold transition-all truncate ${value === option ? "bg-[#881337]/5 text-[#881337]" : "text-gray-500 hover:bg-gray-50"
                      }`}
                  >
                    {option}
                  </button>
                ))
              ) : (
                <div className="px-5 py-2.5 text-[12px] text-gray-400">No results found</div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  const fetchRestaurants = async () => {
    try {
      const data = await RestaurantsService.getAllRestaurants();

      setRestaurants(data);
    } catch (error) {
      console.error("Error fetching restaurants:", error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profile = await authService.getProfile();

        setUserProfile(profile);
      } catch (error) {
        console.log(error, "error");
      }
    };
    fetchProfile();

    fetchRestaurants();
  }, []);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type === "application/json" || file.name.endsWith(".json")) {
      setSelectedFile(file);
      setUploadError("");

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const json = JSON.parse(e.target.result);
          setJsonPreview(json);
        } catch (error) {
          setUploadError("Error parsing JSON file.");
          setJsonPreview(null);
        }
      };
      reader.readAsText(file);
    } else {
      setUploadError("Please select a JSON file (.json)");
      setSelectedFile(null);
      setJsonPreview(null);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleOpenSidePanel = async (panel, restaurant = null) => {
    try {
      setActiveSidePanel(panel);
      setSelectedRestaurant(restaurant);
      setBeerAnalyticsData(null);
      const [dishAnalytics, wineAnalytics, beerAnalytics] = await Promise.all([
        MenuService.getDishAnalytics(restaurant?.uuid),
        MenuService.getWineAnalytics(restaurant?.uuid),
        // A Beer failure must not take down the Food/Wine panel.
        MenuService.getBeerAnalytics(restaurant?.uuid).catch(() => null),
      ]);

      setDishAnalyticsData(dishAnalytics);
      setWineAnalyticsData(wineAnalytics);
      setBeerAnalyticsData(beerAnalytics);
    } catch (error) {
      console.log(error, "error");
    }
  };

  const handleCloseSidePanel = (e) => {
    if (e) e.stopPropagation();
    setActiveSidePanel(null);
    setSelectedRestaurant(null);
  };

  const handleAddRestaurant = () => {
    handleOpenSidePanel("addRestaurant");
  };

  const handleEditRestaurant = (restaurant) => {
    handleOpenSidePanel("editRestaurant", restaurant);
  };

  const handleSaveRestaurant = async (activeSidePanel, formData, error) => {
    console.log("and error:", error);
    try {
      // Uncomment and use the correct API call
      // if (activeSidePanel === "editRestaurant" && selectedRestaurant) {
      //   await RestaurantsService.updateRestaurant(selectedRestaurant.uuid, restaurantData)
      // } else {
      //   await RestaurantsService.createRestaurant(restaurantData)
      // }
      handleCloseSidePanel();
      if (restaurants?.length === 0) {
        setSnackbarMessage(
          "Restaurant added! Now, add your staff and create your menu to unlock full features."
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      } else if (activeSidePanel === "addRestaurant") {
        if (typeof error === "string") {
          setSnackbarMessage(error);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        } else {
          setSnackbarMessage("Restaurant added!");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
      } else if (activeSidePanel === "editRestaurant") {
        setSnackbarMessage("Restaurant updated!");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
      }
      // Refresh the restaurants list
      const data = await RestaurantsService.getAllRestaurants();
      setRestaurants(data);
      // if (callback) callback();
    } catch (error) {
      console.error("Error saving restaurant:", error);
      setSnackbarMessage(
        error.response?.data?.message || "Error saving restaurant"
      );
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleBulkUpload = async () => {
    if (!selectedFile) {
      setUploadError("Please select a file");
      return;
    }
    const role = localStorage.setItem("userRole");
    if (
      selectedFile.type !== "application/json" &&
      !selectedFile.name.endsWith(".json")
    ) {
      setUploadError("Please select a JSON file (.json)");
      return;
    }

    setIsUploading(true);
    setUploadError("");

    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      await bulkUploadLessons(formData, "restaurant");
      setShowBulkUploadModal(false);
      setSelectedFile(null);
      setJsonPreview(null);
      // Refresh the restaurants list
      const data = await RestaurantsService.getAllRestaurants();
      setRestaurants(data);
    } catch (error) {
      setUploadError(
        error.response?.data?.message || "Error uploading restaurants"
      );
    } finally {
      setIsUploading(false);
    }
  };

  const managerAddedRestaurant = restaurants.filter(
    (restaurant) => restaurant?.account_owner?.uuid === user?.uuid
  );

  // Client-side filtered list (search, menu update filter, and sorting)
  const filteredRestaurants = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();

    // Start with all restaurants
    let result = Array.isArray(restaurants) ? restaurants.slice() : [];

    // Search filter
    if (q) {
      result = result.filter((r) => {
        const name = r?.name || "";
        const street = r?.address?.street || "";
        const city = r?.address?.city || "";
        const state = r?.address?.state || "";
        const zip = r?.address?.zip || "";
        const directorName = r?.account_owner?.first_name && r?.account_owner?.last_name
          ? `${r.account_owner.first_name} ${r.account_owner.last_name}`
          : r?.account_owner?.email || "";
        const region = usaStates[state] || state || "";
        const combined = `${name} ${street} ${city} ${state} ${zip} ${directorName} ${region}`.toLowerCase();
        return combined.includes(q);
      });
    }

    // State/Region filter
    if (selectedCity && selectedCity !== "State" && selectedCity !== "Region") {
      const stateCode = Object.keys(usaStates).find((k) => usaStates[k] === selectedCity);
      const selectedLower = String(selectedCity).toLowerCase();
      result = result.filter((r) => {
        const addr = r?.address || {};
        const st = (addr.state || "").toString();
        const cityName = (addr.city || "").toString().toLowerCase();

        if (cityName === selectedLower) return true;
        if (st.toLowerCase() === selectedLower) return true;
        if (stateCode && st === stateCode) return true;
        return false;
      });
    }

    // Menu update period filter (7d, 30d, 90d, Any)
    if (selectedPeriod && selectedPeriod !== "Any") {
      const daysLimit = selectedPeriod === "7d" ? 7 : selectedPeriod === "30d" ? 30 : selectedPeriod === "90d" ? 90 : null;
      if (daysLimit != null) {
        result = result.filter((r) => {
          // Try to parse dashboard.lastMenuUpdated (e.g., "Today", "1 day ago", "3 days ago")
          const lm = r?.dashboard?.lastMenuUpdated;
          let days = null;

          if (lm) {
            const low = String(lm).toLowerCase();
            if (low.includes("today")) days = 0;
            else {
              const m = low.match(/(\d+)\s+day/);
              if (m) days = parseInt(m[1], 10);
            }
          }

          // Fallback: try updatedAt timestamp on restaurant
          if (days == null) {
            const dateStr = r.updatedAt || r?.dashboard?.lastMenuUpdatedDate;
            if (dateStr) {
              const d = new Date(dateStr);
              if (!isNaN(d)) {
                days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
              }
            }
          }

          // If we couldn't determine days, exclude from filtered results
          if (days == null) return false;
          return days <= daysLimit;
        });
      }
    }

    // Tab filter (Active/Inactive)
    if (activeTab === "active") {
      result = result.filter((r) => r.status === "active");
    } else if (activeTab === "inactive") {
      result = result.filter((r) => r.status === "inactive");
    }

    // Director filter
    if (selectedDirector && selectedDirector !== "Director") {
      result = result.filter((r) => {
        const ownerName = r?.account_owner?.first_name && r?.account_owner?.last_name
          ? `${r.account_owner.first_name} ${r.account_owner.last_name}`
          : r?.account_owner?.email;
        return ownerName === selectedDirector;
      });
    }

    // Subscription Type filter
    if (selectedSubscriptionType && selectedSubscriptionType !== "Subscription Type") {
      result = result.filter((r) => {
        const plan = r?.account_owner?.current_subscription?.plan;

        if (selectedSubscriptionType === "Free") {
          return plan === "Free trial";
        }
        if (selectedSubscriptionType === "Single Location") {
          return plan === "Single Location";
        }
        if (selectedSubscriptionType === "Multi Location") {
          return plan === "Multi Location";
        }

        return plan === selectedSubscriptionType;
      });
    }

    // Training Risk filter
    if (selectedRisk && selectedRisk !== "Training Risk") {
      result = result.filter((r) => {
        const score = r?.dashboard?.riskScore || 0;
        if (selectedRisk === "High Priority") return score > 70;
        if (selectedRisk === "Medium Alert") return score > 30 && score <= 70;
        if (selectedRisk === "Stable") return score <= 30;
        return true;
      });
    }

    // Sorting
    const sortKey = selectedSort || "Alphabetical";
    if (sortKey === "Alphabetical" || sortKey === "Sort by: Alphabetical") {
      result.sort((a, b) => (a?.name || "").localeCompare(b?.name || ""));
    } else if (sortKey === "Last Updated" || sortKey === "Sort by: Last Updated") {
      const getDays = (r) => {
        const lm = r?.dashboard?.lastMenuUpdated;
        let days = null;
        if (lm) {
          const low = String(lm).toLowerCase();
          if (low.includes("today")) days = 0;
          else {
            const m = low.match(/(\d+)\s+day/);
            if (m) days = parseInt(m[1], 10);
          }
        }
        if (days == null) {
          const dateStr = r.updatedAt;
          if (dateStr) {
            const d = new Date(dateStr);
            if (!isNaN(d)) days = Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
          }
        }
        return days == null ? Number.POSITIVE_INFINITY : days;
      };
      // most recently updated first (smaller days -> earlier)
      result.sort((a, b) => getDays(a) - getDays(b));
    } else if (sortKey === "Training Completion" || sortKey === "Sort by: Training Completion") {
      result.sort((a, b) => (b.dashboard?.lessonCompletionPercentage || 0) - (a.dashboard?.lessonCompletionPercentage || 0));
    }


    return result;
  }, [restaurants, searchTerm, selectedPeriod, selectedSort, selectedCity, activeTab, selectedDirector, selectedSubscriptionType, selectedRisk]);

  // Calculate stats for Super Admin
  const superAdminStats = useMemo(() => {
    const totalRestaurants = restaurants.length;
    const activeRestaurants = restaurants.filter(r => r?.active !== false).length;
    const activePercentage = totalRestaurants > 0 ? Math.round((activeRestaurants / totalRestaurants) * 100) : 0;

    // Calculate total employees from all restaurants (if available in data)
    const totalEmployees = dashboardStats.totalEmployees || 0;

    // Format last menu update
    const lastUpdateDays = dashboardStats.lastMenuUpdateInDays || 0;
    let lastUpdateDisplay = `${lastUpdateDays}d ago`;
    if (lastUpdateDays === 0) {
      lastUpdateDisplay = "Today";
    } else if (lastUpdateDays >= 30) {
      const months = Math.floor(lastUpdateDays / 30);
      const days = lastUpdateDays % 30;
      if (days === 0) {
        lastUpdateDisplay = `${months}mo ago`;
      } else {
        lastUpdateDisplay = `${months}mo ${days}d ago`;
      }
    }

    return {
      totalRestaurants,
      activeRestaurants: `${activePercentage}%`,
      totalEmployees,
      avgCompletion: `${Math.round(dashboardStats.trainingRate || 0)}%`,
      avgEngagement: `${Math.round(dashboardStats.engagementRate || 0)}%`,
      overdueRestaurants: dashboardStats.missedUsersCount || 0,
      lastMenuUpdate: lastUpdateDisplay,
    };
  }, [restaurants, dashboardStats]);

  const stats = userRole === "super_admin" ? [
    { label: "Total Restaurants", value: superAdminStats.totalRestaurants },
    { label: "Active Restaurants", value: superAdminStats.activeRestaurants, color: "text-[#881337]" },
    { label: "Total Employees", value: superAdminStats.totalEmployees.toLocaleString() },
    { label: "Avg Completion", value: superAdminStats.avgCompletion, color: "text-[#881337]" },
    { label: "Avg Engagement (30d)", value: superAdminStats.avgEngagement, color: "text-[#881337]" },
    { label: "Overdue Restaurants", value: superAdminStats.overdueRestaurants, color: "text-[#881337]" },
    { label: "Most Recent Menu Update", value: superAdminStats.lastMenuUpdate, color: "text-[#881337]" },
  ] : [
    { label: "Restaurants in View", value: filteredRestaurants.length },
    { label: "Avg Training Completion", value: `${dashboardStats.trainingRate?.toFixed(0) || 0}%`, color: "text-[#881337]" },
    { label: "Avg Engagement (30d)", value: `${dashboardStats.engagementRate?.toFixed(0) || 0}%`, color: "text-[#881337]" },
    { label: "Total Overdue", value: dashboardStats.missedUsersCount || 0, color: "text-[#881337]" },
    { label: "Most Recent Menu Update", value: `${dashboardStats.lastMenuUpdateInDays || 0}d ago`, color: "text-[#881337]" },
  ];

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const data = await DashboardService.getAllStats();
        console.log("Dashboard data:", data);
        setDashboardStats({
          trainingRate: data.trainingRate || 0,
          engagementRate: data.engagementRate || 0,
          missedUsersCount: data.missedUsersCount || 0,
          lastMenuUpdateInDays: data.lastMenuUpdateInDays || data.lastMenuUpdate || 0,
          averageChapterScore: data.averageChapterScore || 0,
          totalRestaurants: data.totalRestaurants || 0,
          totalEmployees: data.totalEmployees || 0,
          activeRestaurantsCount: data.activeRestaurantsCount || 0,
        });
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };
    fetchAllData();
  }, []);

  // Get unique directors and subscription types for filters
  const uniqueDirectors = useMemo(() => {
    const directors = new Set();
    restaurants.forEach(r => {
      if (r?.account_owner?.first_name && r?.account_owner?.last_name) {
        directors.add(`${r.account_owner.first_name} ${r.account_owner.last_name}`);
      } else if (r?.account_owner?.email) {
        directors.add(r.account_owner.email);
      }
    });
    return Array.from(directors).sort();
  }, [restaurants]);

  const uniqueSubscriptionTypes = useMemo(() => {
    // Only return the 3 generic types requested
    return ["Free", "Single Location", "Multi Location"];
  }, []);

  // Group restaurants by owner for super_admin view
  const restaurantsByOwner = useMemo(() => {
    if (userRole !== "super_admin") return [];

    const grouped = {};
    filteredRestaurants.forEach(restaurant => {
      const owner = restaurant?.account_owner;
      if (owner) {
        const ownerKey = owner.uuid || owner.email || 'unknown';
        if (!grouped[ownerKey]) {
          grouped[ownerKey] = {
            owner: owner,
            restaurants: []
          };
        }
        grouped[ownerKey].restaurants.push(restaurant);
      }
    });

    return Object.values(grouped).sort((a, b) => {
      const nameA = `${a.owner.first_name || ''} ${a.owner.last_name || ''}`.trim() || a.owner.email || '';
      const nameB = `${b.owner.first_name || ''} ${b.owner.last_name || ''}`.trim() || b.owner.email || '';
      return nameA.localeCompare(nameB);
    });
  }, [filteredRestaurants, userRole]);

  // State for expanded owners
  const [expandedOwners, setExpandedOwners] = useState(new Set());

  const toggleOwner = (ownerKey) => {
    setExpandedOwners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerKey)) {
        newSet.delete(ownerKey);
      } else {
        newSet.add(ownerKey);
      }
      return newSet;
    });
  };

  // Pagination Logic
  const totalItems = restaurantsByOwner.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedOwners = restaurantsByOwner.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className={`flex flex-col min-h-screen ${userRole === "director" || userRole === "super_admin" ? "bg-[#FFF9F2]" : "bg-background"}`}>
      <div className="flex-1 overflow-auto">
        <main className={`p-4 sm:p-5 lg:p-6 max-w-[1400px] mx-auto ${userRole === "director" || userRole === "super_admin" ? "animate-in fade-in duration-500" : ""}`}>
          {userRole === "super_admin" ? (
            /* Super Admin Dashboard Header, Filters & Stats */
            <div className="space-y-6 mb-8">
              {/* Header Row */}
              <div className="flex justify-between items-center mb-4">
                <div>
                  <h1 className="text-2xl sm:text-3xl font-extrabold text-[#881337] tracking-tight">
                    Restaurant Management
                  </h1>
                  <p className="text-sm ml-2 text-start text-gray-500 mt-1">Super Admin View</p>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                  >
                    <Upload size={20} className="mr-2" />
                    Bulk Upload
                  </button>
                  <button
                    onClick={handleAddRestaurant}
                    className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Restaurant
                  </button>
                </div>
              </div>

              {/* Stats Grid - Show 7 cards */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-4">
                {stats.map((stat, i) => (
                  <div
                    key={i}
                    className="bg-white p-6 rounded-2xl shadow-sm border border-gray-50 flex flex-col items-center justify-center text-center hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-[12px] font-bold text-gray-400 tracking-widest mb-2">
                      {stat.label}
                    </span>
                    <span className={`text-3xl font-extrabold tracking-tight ${stat.color || "text-[#1F2937]"}`}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>

              {/* Filter Card */}
              <div className="bg-white rounded-[2rem] p-8 border border-gray-100 shadow-sm space-y-8">
                {/* Row 1: Status Pills on left, Search on right */}
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-6">
                  <div className="flex bg-gray-50/50 p-1.5 rounded-[1rem] gap-1 border border-gray-100/50">
                    {["Active", "Inactive", "All"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => handleTabChange(tab.toLowerCase())}
                        className={`px-8 py-2.5 rounded-[0.75rem] text-[13px] font-bold transition-all duration-300 whitespace-nowrap ${activeTab === tab.toLowerCase()
                          ? "bg-[#881337] text-white shadow-lg shadow-[#881337]/20"
                          : "text-gray-400 hover:text-gray-600 hover:bg-white/80"
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full md:w-[480px] group">
                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4.5 h-4.5 text-gray-300 group-focus-within:text-[#881337] transition-colors" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search restaurants, directors, or regions..."
                      className="w-full bg-gray-50/30 border border-gray-200 rounded-[1.25rem] py-3.5 pl-12 pr-5 text-[13px] font-medium focus:outline-none focus:ring-4 focus:ring-[#881337]/5 focus:border-[#881337] transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>

                {/* Row 2: Selectors Grid */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                  <CustomDropdown
                    label="Region"
                    value={selectedCity === "State" ? "Region" : selectedCity}
                    options={["Region", ...Object.values(usaStates)]}
                    onChange={setSelectedCity}
                    className="w-full"
                    searchable={true}
                  />
                  <CustomDropdown
                    label="Director"
                    value={selectedDirector}
                    options={["Director", ...uniqueDirectors]}
                    onChange={setSelectedDirector}
                    className="w-full"
                    searchable={true}
                  />
                  <CustomDropdown
                    label="Subscription Type"
                    value={selectedSubscriptionType}
                    options={["Subscription Type", ...uniqueSubscriptionTypes]}
                    onChange={setSelectedSubscriptionType}
                    className="w-full"
                  />
                  <CustomDropdown
                    label="Training Risk"
                    value={selectedRisk}
                    options={["Training Risk", "High Priority", "Medium Alert", "Stable"]}
                    onChange={setSelectedRisk}
                    className="w-full"
                    maxHeight="max-h-none"
                  />
                </div>

                {/* Separator Line */}
                <div className="h-[1px] bg-gray-100/80 -mx-8"></div>

                {/* Row 3: Sort by */}
                <div className="flex justify-start">
                  <CustomDropdown
                    label="Sort by"
                    value={selectedSort.startsWith("Sort by:") ? selectedSort : `Sort by: ${selectedSort}`}
                    options={["Sort by: Alphabetical", "Sort by: Last Updated", "Sort by: Training Completion"]}
                    onChange={setSelectedSort}
                    className="w-full md:w-[320px]"
                  />
                </div>
              </div>
            </div>
          ) : userRole === "director" ? (
            /* Director Dashboard Header, Filters & Stats */
            <div className="space-y-6 mb-8">
              {/* Header Row */}
              <div className="flex justify-between items-center mb-4">
                <h1 className="text-2xl sm:text-3xl font-extrabold text-[#881337] tracking-tight">
                  Restaurant Management
                </h1>

                {/* Add Restaurant button for Directors (show when subscription has room) */}
                {!loading && ((userProfile?.current_subscription?.locations == null) || (restaurants.length < userProfile?.current_subscription?.locations)) && (
                  <button
                    onClick={handleAddRestaurant}
                    className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 4v16m8-8H4"
                      />
                    </svg>
                    Add Restaurant
                  </button>
                )}

              </div>

              {/* Precise Filter Card (Based on Image) */}
              <div className="bg-[#F9FAFB]/60 backdrop-blur-sm rounded-3xl p-6 sm:p-8 border border-[#F3E8D6] shadow-sm space-y-6">
                {/* Row 1: Tabs on left, Search on right */}
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-4">
                  <div className="bg-gray-100/50 p-1 rounded-xl flex gap-1 border border-gray-100 w-full md:w-auto">
                    {["Active", "Inactive", "All"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => handleTabChange(tab.toLowerCase())}
                        className={`flex-1 md:flex-none px-2 sm:px-6 py-2 rounded-lg text-xs font-bold transition-all duration-300 whitespace-nowrap ${activeTab === tab.toLowerCase()
                          ? "bg-white text-[#881337] shadow-sm"
                          : "text-gray-400 hover:text-gray-600"
                          }`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>

                  <div className="relative w-full md:w-[380px] group">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-300 group-focus-within:text-[#881337] transition-colors" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search restaurants..."
                      className="w-full bg-white border border-gray-100 rounded-lg py-2.5 pl-10 pr-4 text-xs focus:outline-none focus:ring-2 focus:ring-[#881337]/5 focus:border-[#881337] transition-all placeholder:text-gray-300"
                    />
                  </div>
                </div>

                {/* Row 2: Selectors with Styled Options */}
                <div className="flex flex-wrap gap-4">
                  <CustomDropdown
                    label="State"
                    value={selectedCity}
                    options={["State", ...Object.values(usaStates)]}
                    onChange={setSelectedCity}
                    className="w-full sm:w-auto min-w-[170px]"
                    searchable={true}
                  />
                  {/* <CustomDropdown
                    label="Cuisine"
                    value={selectedCuisine}
                    options={["Cuisine", "Italian", "French", "Mexican", "Asian", "American"]}
                    onChange={setSelectedCuisine}
                    className="w-full sm:w-auto min-w-[170px]"
                  />
                  <CustomDropdown
                    label="Training Risk"
                    value={selectedRisk}
                    options={["Training Risk", "High Priority", "Medium Alert", "Stable"]}
                    onChange={setSelectedRisk}
                    className="w-full sm:w-auto min-w-[170px]"
                  /> */}
                </div>

                {/* Row 3: Menu Updated on left, Sort on right */}
                <div className="flex flex-col md:flex-row justify-between items-center gap-4">
                  <div className="flex flex-col sm:flex-row sm:items-center bg-white border border-orange-50/50 rounded-xl p-1 shadow-sm w-full md:w-auto gap-2 sm:gap-0">
                    <span className="px-4 py-1 sm:py-0 text-[12px] text-gray-400 font-extrabold tracking-wider sm:border-r border-orange-50/20 text-center sm:text-left">
                      Menu Updated:
                    </span>
                    <div className="flex flex-wrap justify-center sm:justify-start gap-1 ml-0 sm:ml-1">
                      {["7d", "30d", "90d", "Any"].map((period) => (
                        <button
                          key={period}
                          onClick={() => setSelectedPeriod(period)}
                          className={`px-3 sm:px-4 py-1.5 text-[12px] font-extrabold rounded-lg transition-all duration-300 flex-1 sm:flex-none ${selectedPeriod === period
                            ? "bg-[#881337] text-white shadow-md shadow-[#881337]/20"
                            : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                            }`}
                        >
                          {period}
                        </button>
                      ))}
                    </div>
                  </div>

                  <CustomDropdown
                    label="Sort"
                    value={selectedSort}
                    options={["Alphabetical", "Last Updated"]}
                    onChange={setSelectedSort}
                    className="w-full md:w-[280px]"
                  />
                </div>
              </div>

              {/* Compact Stats Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                {stats.map((stat, i) => (
                  <div
                    key={i}
                    className="bg-white p-6 rounded-2xl shadow-sm border border-gray-50 flex flex-col items-center justify-center text-center hover:shadow-md transition-all duration-300"
                  >
                    <span className="text-[9px] font-bold text-gray-400  tracking-widest mb-2">
                      {stat.label}
                    </span>
                    <span className={`text-3xl font-extrabold tracking-tight ${stat.color || "text-[#1F2937]"}`}>
                      {stat.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Manager and Employee View */
            (userRole === "manager" || userRole === "employee") && (
              <div className="mb-6 bg-white p-4 sm:p-5 lg:p-6 border border-gray-200 rounded-lg shadow-sm">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-semibold mb-4 text-left text-textcolor">
                  Your Restaurants
                </h2>
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-4 space-y-2 sm:space-y-0">
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={() => handleTabChange("active")}
                      className={`px-3 sm:px-4 py-2 rounded-md ${activeTab === "active"
                        ? "bg-textcolor text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300"
                        }`}
                    >
                      Active
                    </button>
                    <button
                      onClick={() => handleTabChange("inactive")}
                      className={`px-3 sm:px-4 py-2 rounded-md ${activeTab === "inactive"
                        ? "bg-textcolor text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300"
                        }`}
                    >
                      Inactive
                    </button>
                    <button
                      onClick={() => handleTabChange("all")}
                      className={`px-3 sm:px-4 py-2 rounded-md ${activeTab === "all"
                        ? "bg-textcolor text-white shadow-md"
                        : "bg-white text-gray-700 border border-gray-300"
                        }`}
                    >
                      All
                    </button>
                  </div>
                  <div className="flex gap-2">
                    {userRole === "super_admin" && (
                      <button
                        onClick={() => setShowBulkUploadModal(true)}
                        className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                      >
                        <Upload size={20} className="mr-2" />
                        Bulk Upload
                      </button>
                    )}
                    {/* Show Add Restaurant button based on role */}
                    {userRole === "manager" ? (
                      // Managers have special restrictions based on their plan
                      (() => {
                        const currentPlan = userProfile?.current_subscription?.plan;
                        const managerRestaurantCount = managerAddedRestaurant?.length || 0;

                        // Determine if manager can add a restaurant
                        let canAddRestaurant = false;

                        if (currentPlan === "Free trial" || currentPlan === "Single Location") {
                          // Free and Single Location: only 1 restaurant allowed
                          canAddRestaurant = managerRestaurantCount === 0;
                        } else if (currentPlan === "Multi Location") {
                          // Multi Location: manager can add 1 more if they have any
                          // But maximum 2 restaurants total for managers
                          const maxLocations = userProfile?.current_subscription?.locations || 1;
                          canAddRestaurant = managerRestaurantCount < Math.min(maxLocations, 2);
                        } else {
                          // Default: allow 1 restaurant if they have none
                          canAddRestaurant = managerRestaurantCount === 0;
                        }

                        return canAddRestaurant && !loading ? (
                          <button
                            onClick={handleAddRestaurant}
                            className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                              />
                            </svg>
                            Add Restaurant
                          </button>
                        ) : null;
                      })()
                    ) : (
                      // Other roles (director, etc.) - check subscription limits
                      !loading &&
                      restaurants.length < (userProfile?.current_subscription?.locations || 0) && (
                        <button
                          onClick={handleAddRestaurant}
                          className="px-4 py-2 bg-textcolor text-white rounded-md flex items-center hover:bg-textcolor/90 transition-colors shadow-sm"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-5 w-5 mr-2"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 4v16m8-8H4"
                            />
                          </svg>
                          Add Restaurant
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            )
          )}

          {loading ? (
            <div className="flex min-h-screen items-center justify-center">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-200 border-t-red-500"></div>
            </div>
          ) : userRole === "super_admin" ? (
            /* Owner Grouped View for Super Admin */
            <div className="space-y-4">
              {paginatedOwners.map((group) => {
                const ownerKey = group.owner.uuid || group.owner.email || 'unknown';
                const isExpanded = expandedOwners.has(ownerKey);
                const ownerName = `${group.owner.first_name || ''} ${group.owner.last_name || ''}`.trim() || 'Unknown Owner';
                const ownerEmail = group.owner.email || '';

                // Aggregated stats for the owner
                const totalRestaurantsOwner = group.restaurants.length;
                const activeCount = group.restaurants.filter(r => r.status === "active").length;
                const activePerc = totalRestaurantsOwner > 0 ? Math.round((activeCount / totalRestaurantsOwner) * 100) : 0;
                const totalEmps = group.restaurants.reduce((acc, r) => acc + (r.employees?.length || 0), 0);
                const avgComp = totalRestaurantsOwner > 0
                  ? Math.round(group.restaurants.reduce((acc, r) => acc + (r.dashboard?.lessonCompletionPercentage || 0), 0) / totalRestaurantsOwner)
                  : 0;
                const totalOverdue = group.restaurants.reduce((acc, r) => acc + (r.dashboard?.overDueEmployees || 0), 0);
                const overduePerc = totalEmps > 0 ? ((totalOverdue / totalEmps) * 100).toFixed(1) : "0.0";

                const avgEngagement = totalRestaurantsOwner > 0
                  ? (group.restaurants.reduce((acc, r) => acc + (r.dashboard?.engagementRate || 0), 0) / totalRestaurantsOwner / 100).toFixed(2)
                  : "0.00";

                const freshnessDays = group.restaurants.reduce((min, r) => {
                  const daysStr = r.dashboard?.lastMenuUpdated;
                  if (!daysStr) return min;
                  let days = 999;
                  if (daysStr === "Today") days = 0;
                  else if (daysStr === "1 day ago") days = 1;
                  else {
                    const m = daysStr.match(/(\d+)/);
                    if (m) days = parseInt(m[1], 10);
                  }
                  return Math.min(min, days);
                }, 999);
                const freshnessDisplay = freshnessDays === 999 ? "—" : `${freshnessDays}d`;

                const newItemsCount = group.restaurants.reduce((acc, r) => acc + (r.dashboard?.newItemsCount || 0), 0);

                const avgRiskScore = totalRestaurantsOwner > 0
                  ? Math.round(group.restaurants.reduce((acc, r) => acc + (r.dashboard?.riskScore || 0), 0) / totalRestaurantsOwner)
                  : 0;

                return (
                  <div key={ownerKey} className="group relative bg-white border border-gray-100 rounded-2xl mb-4 hover:shadow-xl hover:shadow-gray-200/40 transition-all duration-300 overflow-hidden">
                    {/* Owner Card Ribbon */}
                    <div
                      onClick={() => toggleOwner(ownerKey)}
                      className="py-4 px-6 flex flex-wrap lg:flex-nowrap items-center cursor-pointer"
                    >
                      {/* Section 1: User Info */}
                      <div className="flex items-center gap-4 min-w-[240px] flex-shrink-0">
                        <div className="relative">
                          <div className="w-12 h-12 rounded-full border border-gray-100 shadow-sm overflow-hidden bg-gray-50 flex items-center justify-center font-bold text-gray-400 text-sm">
                            {group.owner.image_url ? (
                              <img
                                src={process.env.REACT_APP_IMAGE_BASE_URL + group.owner.image_url}
                                alt="avatar"
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              `${group.owner.first_name?.[0] || ""}${group.owner.last_name?.[0] || ""}`
                            )}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <h4 className="text-[15px] font-bold text-[#111827] truncate mb-0.5">
                            {ownerName}
                          </h4>
                          <p className="text-[12px] text-gray-400 font-medium truncate leading-tight">
                            {ownerEmail}
                          </p>
                        </div>
                      </div>

                      {/* Section 2: Full Stats Grid */}
                      <div className="flex-1 flex items-center justify-between px-8 gap-4">

                        {/* Active Restaurants */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="w-4 h-4 rounded-full border border-gray-200 flex items-center justify-center">
                              <span className="text-[9px] text-gray-400 font-bold">?</span>
                            </div>
                            <span className="text-[15px] font-bold text-[#111827]">{totalRestaurantsOwner}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight whitespace-nowrap">
                            {activeCount} active · {activePerc}%
                          </p>
                        </div>

                        {/* Learners */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Users size={16} className="text-gray-400" />
                            <span className="text-[15px] font-bold text-[#111827]">{totalEmps}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight">learners</p>
                        </div>

                        {/* Avg Completion */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="w-4 h-4 rounded-full border border-gray-200 flex items-center justify-center">
                              <span className="text-[9px] text-rose-500 font-bold">?</span>
                            </div>
                            <span className="text-[15px] font-bold text-[#111827]">{avgComp}%</span>
                            {/* <div className="flex flex-col items-center ml-0.5 leading-none">
                              <span className="text-[8px] text-emerald-500 font-bold">↑</span>
                              <span className="text-[7px] text-emerald-500 font-bold">3.2%</span>
                            </div> */}
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight whitespace-nowrap">avg completion</p>
                        </div>

                        {/* Overdue */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="text-rose-600">
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
                            </div>
                            <span className="text-[15px] text-[#111827] font-bold">{totalOverdue}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight">{overduePerc}% overdue</p>
                        </div>

                        {/* Engagement */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="text-indigo-500">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 3v18h18" /><path d="m19 9-5 5-4-4-3 3" /></svg>
                            </div>
                            <span className="text-[15px] font-bold text-[#111827]">{avgEngagement}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight">engagement</p>
                        </div>

                        {/* Freshness */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <PiForkKnifeFill size={16} className="text-orange-500" />
                            <span className="text-[15px] font-bold text-[#111827]">{freshnessDisplay}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight">freshness</p>
                        </div>

                        {/* New Items */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="w-4 h-4 rounded-full border border-gray-200 flex items-center justify-center">
                              <span className="text-[9px] text-gray-400 font-bold">?</span>
                            </div>
                            <span className="text-[15px] font-bold text-[#111827]">{newItemsCount}</span>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight whitespace-nowrap">new items</p>
                        </div>

                        {/* Risk Score */}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2 mb-0.5">
                            <div className="flex-1 h-3 flex items-end min-w-[50px]">
                              <svg className={`w-full h-full ${avgRiskScore > 70 ? 'text-rose-500' : avgRiskScore > 30 ? 'text-amber-500' : 'text-emerald-500'}`} viewBox="0 0 40 20" preserveAspectRatio="none">
                                <path d="M0 18 L10 12 L20 15 L30 5 L40 2" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                              </svg>
                            </div>
                          </div>
                          <p className="text-[11px] font-medium text-gray-400 lowercase tracking-tight whitespace-nowrap">risk score</p>
                        </div>

                      </div>

                      {/* Section 3: Action */}
                      <div className="flex items-center justify-end pl-6">
                        <div className={`text-gray-400 transition-all ${isExpanded ? 'rotate-180' : ''}`}>
                          <ChevronDown size={20} />
                        </div>
                      </div>
                    </div>

                    {/* Restaurants List */}
                    {isExpanded && (
                      <div className="border-t border-gray-100 p-6 bg-gray-50/30">
                        <RestaurantList
                          restaurants={group.restaurants}
                          activeTab={activeTab}
                          userRole={userRole}
                          onViewEmployees={(restaurant) =>
                            handleOpenSidePanel("viewEmployees", restaurant)
                          }
                          onManageMenu={(restaurant) =>
                            handleOpenSidePanel("manageMenu", restaurant)
                          }
                          onLessonProgress={(restaurant) =>
                            handleOpenSidePanel("lessonProgress", restaurant)
                          }
                          onManageRestaurant={(restaurant) =>
                            handleOpenSidePanel("manageRestaurant", restaurant)
                          }
                          onEditRestaurant={handleEditRestaurant}
                        />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Pagination UI */}
              <div className="mt-12 flex flex-col md:flex-row items-center justify-between gap-6 pb-10">
                {/* Left side: Items per page */}
                <div className="flex items-center gap-3">
                  <span className="text-[13px] font-medium text-gray-400">Show</span>
                  <div className="relative group">
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="appearance-none bg-white border border-gray-200 rounded-xl px-4 py-2 pr-10 text-[13px] font-bold text-[#111827] focus:outline-none focus:ring-4 focus:ring-[#881337]/5 focus:border-[#881337] transition-all cursor-pointer"
                    >
                      {[5, 10, 25, 50].map(val => (
                        <option key={val} value={val}>{val}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                  </div>
                  <span className="text-[13px] font-medium text-gray-400">results per page</span>
                </div>

                {/* Center: Numerical Pages */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`p-2.5 rounded-xl border border-gray-100 transition-all ${currentPage === 1 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50 text-gray-500'}`}
                  >
                    <ChevronLeft size={18} />
                  </button>

                  <div className="flex items-center gap-1.5">
                    {[...Array(totalPages)].map((_, idx) => {
                      const pageNum = idx + 1;
                      if (totalPages > 7) {
                        if (pageNum === 1 || pageNum === totalPages || (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)) {
                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`w-10 h-10 rounded-xl font-bold text-[13px] transition-all ${currentPage === pageNum
                                ? 'bg-[#881337] text-white shadow-lg shadow-[#881337]/20'
                                : 'bg-white border border-gray-100 text-gray-500 hover:bg-gray-50'
                                }`}
                            >
                              {pageNum}
                            </button>
                          );
                        } else if (pageNum === 2 || pageNum === totalPages - 1) {
                          return <span key={pageNum} className="px-1 text-gray-300">...</span>;
                        }
                        return null;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-xl font-bold text-[13px] transition-all ${currentPage === pageNum
                            ? 'bg-[#881337] text-white shadow-lg shadow-[#881337]/20'
                            : 'bg-white border border-gray-100 text-gray-500 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`p-2.5 rounded-xl border border-gray-100 transition-all ${currentPage === totalPages ? 'opacity-30 cursor-not-allowed' : 'hover:bg-gray-50 text-gray-500'}`}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>

                {/* Right side: Stats */}
                <div className="text-[13px] font-medium text-gray-400">
                  Showing <span className="text-[#111827] font-bold">{totalItems === 0 ? 0 : startIndex + 1}-{endIndex}</span> of <span className="text-[#111827] font-bold">{totalItems}</span> directors
                </div>
              </div>

              {restaurantsByOwner.length === 0 && (
                <div className="text-center p-8 bg-white rounded-lg border border-gray-200">
                  <p className="text-gray-500">No restaurants found</p>
                </div>
              )}
            </div>
          ) : (
            <RestaurantList
              restaurants={filteredRestaurants}
              activeTab={activeTab}
              userRole={userRole}
              onViewEmployees={(restaurant) =>
                handleOpenSidePanel("viewEmployees", restaurant)
              }
              onManageMenu={(restaurant) =>
                handleOpenSidePanel("manageMenu", restaurant)
              }
              onLessonProgress={(restaurant) =>
                handleOpenSidePanel("lessonProgress", restaurant)
              }
              onManageRestaurant={(restaurant) =>
                handleOpenSidePanel("manageRestaurant", restaurant)
              }
              onEditRestaurant={handleEditRestaurant}
            />
          )}
        </main>
      </div>

      {/* Side Panels */}
      {activeSidePanel === "viewEmployees" && (
        <ViewEmployeesPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
        />
      )}

      {activeSidePanel === "lessonProgress" && (
        <LessonProgressPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
        />
      )}

      {activeSidePanel === "manageRestaurant" && (
        <ManageRestaurantPanel
          restaurant={selectedRestaurant}
          onClose={handleCloseSidePanel}
          fetchData={fetchRestaurants}
        />
      )}

      {activeSidePanel === "manageMenu" &&
        dishAnalyticsData?.dishTypesWithPercentages?.length > 0 &&
        wineAnalyticsData && (
          <ManageMenuPanel
            restaurant={selectedRestaurant}
            onClose={handleCloseSidePanel}
            dishAnalyticsData={dishAnalyticsData}
            wineAnalyticsData={wineAnalyticsData}
            beerAnalyticsData={beerAnalyticsData}
            userRole={userRole}
          />
        )}

      {(activeSidePanel === "addRestaurant" ||
        activeSidePanel === "editRestaurant") && (
          <AddRestaurantPanel
            restaurant={
              activeSidePanel === "editRestaurant" ? selectedRestaurant : null
            }
            onSave={(formData, error) =>
              handleSaveRestaurant(activeSidePanel, formData, error)
            }
            onClose={handleCloseSidePanel}
          />
        )}

      {/* Snackbar for success/error messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex">
          <div
            className="fixed inset-0 bg-black opacity-30"
            onClick={() => setShowBulkUploadModal(false)}
          ></div>
          <div className="fixed inset-y-0 right-0 w-full md:w-1/2 lg:w-[50%] bg-white border-l border-gray-200 shadow-lg z-50 overflow-y-auto">
            <div className="p-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-textcolor">
                Bulk Upload Restaurants
              </h2>
              <button
                onClick={() => setShowBulkUploadModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 text-textcolor"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium mb-2">Upload JSON File</h3>
                  <p className="text-sm text-gray-500">
                    Upload a JSON file containing restaurant information. Make
                    sure to follow the correct format.
                  </p>
                </div>

                <div
                  className={`mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md ${dragActive ? "bg-gray-100" : ""
                    }`}
                  onDragEnter={handleDrag}
                  onDragLeave={handleDrag}
                  onDragOver={handleDrag}
                  onDrop={handleDrop}
                >
                  <div className="space-y-1 text-center">
                    <svg
                      className="mx-auto h-12 w-12 text-gray-400"
                      stroke="currentColor"
                      fill="none"
                      viewBox="0 0 48 48"
                      aria-hidden="true"
                    >
                      <path
                        d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                        strokeWidth={2}
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-primary hover:text-primary-dark focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-primary"
                      >
                        <span>
                          {selectedFile ? selectedFile.name : "Upload a file"}
                        </span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept=".json"
                          className="sr-only"
                          onChange={(e) => handleFile(e.target.files[0])}
                        />
                      </label>
                      <p className="pl-1">or drag and drop</p>
                    </div>
                    <p className="text-xs text-gray-500">JSON up to 10MB</p>
                  </div>
                </div>

                {jsonPreview && (
                  <div>
                    <h4 className="text-md font-medium mb-2">File Preview</h4>
                    <pre className="bg-gray-100 p-4 rounded-md text-xs overflow-auto max-h-60">
                      <code>{JSON.stringify(jsonPreview, null, 2)}</code>
                    </pre>
                  </div>
                )}

                {uploadError && (
                  <div className="p-3 bg-red-50 text-red-600 rounded-md text-sm">
                    {uploadError}
                  </div>
                )}

                <div className="flex justify-between items-center space-x-2 pt-4 border-t border-gray-200">
                  <a
                    href="./restaurants.json"
                    download
                    className="inline-flex items-center justify-center font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 bg-textcolor text-white hover:bg-gray-400 px-4 py-2 text-base"
                    style={{ textDecoration: "none" }}
                  >
                    Download Template
                  </a>
                  <div className="flex space-x-2">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
                      onClick={() => setShowBulkUploadModal(false)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md bg-primary text-white hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleBulkUpload}
                      disabled={!selectedFile || isUploading}
                    >
                      {isUploading ? "Uploading..." : "Upload Restaurants"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
